import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation, Link, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, CreditCard, DollarSign, Activity, Settings,
  LogOut, Menu, X, Search, ChevronRight, ChevronDown, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Clock, UserPlus, Eye, Edit, Trash2,
  Mail, Phone, Building2, Calendar, MoreVertical, RefreshCw, Download,
  Shield, Key, ExternalLink, ArrowLeft, Filter, ChevronLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = 'admin_auth_token';

// Admin API instance
const AdminAPI = axios.create({
  baseURL: `${BACKEND_URL}/api/admin`,
  withCredentials: true,
  timeout: 30000,
});

// Add auth token to requests
AdminAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Admin Auth Context
const AdminAuthContext = createContext(null);

export const useAdminAuth = () => useContext(AdminAuthContext);

// Admin Auth Provider
const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await AdminAPI.get('/auth/me');
      setAdmin(response.data);
    } catch {
      setAdmin(null);
      localStorage.removeItem(ADMIN_TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await AdminAPI.post('/auth/login', { email, password });
    localStorage.setItem(ADMIN_TOKEN_KEY, response.data.session_token);
    setAdmin(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await AdminAPI.post('/auth/logout');
    } catch (e) {
      console.error('Logout error:', e);
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdmin(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout, checkAuth }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Protected Admin Route
const ProtectedAdminRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN LOGIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-10 h-10 text-emerald-500" />
            <h1 className="text-2xl font-bold text-white">CONTRACTOR OVERWATCH</h1>
          </div>
          <p className="text-slate-400">Super Admin Dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-xl p-8 shadow-xl border border-slate-700">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="admin@contractoroverwatch.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </form>

        <p className="text-center text-slate-500 text-sm mt-6">
          Staff access only. Unauthorized access is prohibited.
        </p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN LAYOUT
// ═══════════════════════════════════════════════════════════════════════════════

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/customers', icon: Users, label: 'Customers' },
    { path: '/admin/subscriptions', icon: CreditCard, label: 'Subscriptions' },
    { path: '/admin/revenue', icon: DollarSign, label: 'Revenue' },
    { path: '/admin/impersonation-logs', icon: Eye, label: 'Access Logs' },
    { path: '/admin/activity', icon: Activity, label: 'Activity' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen bg-slate-900 flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-800 border-r border-slate-700 transition-all duration-300 flex flex-col flex-shrink-0`}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-emerald-500 flex-shrink-0" />
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-white text-sm">CONTRACTOR</h1>
                <p className="text-emerald-500 text-xs font-semibold">OVERWATCH ADMIN</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-emerald-600 text-white'
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-slate-700">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white font-semibold text-sm">
                  {admin?.name?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{admin?.name}</p>
                  <p className="text-slate-400 text-xs">{admin?.role}</p>
                </div>
              </div>
              <button onClick={handleLogout} className="text-slate-400 hover:text-white">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button onClick={handleLogout} className="w-full flex justify-center text-slate-400 hover:text-white">
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-4 -right-3 w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center text-slate-400 hover:text-white border border-slate-600"
          style={{ left: sidebarOpen ? '248px' : '68px' }}
        >
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden">
        {children}
      </main>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DASHBOARD PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const DashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [revenueChart, setRevenueChart] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [metricsRes, chartRes, activityRes] = await Promise.all([
        AdminAPI.get('/dashboard/metrics'),
        AdminAPI.get('/dashboard/revenue-chart'),
        AdminAPI.get('/dashboard/activity')
      ]);
      setMetrics(metricsRes.data);
      setRevenueChart(chartRes.data);
      setActivity(activityRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ icon: Icon, label, value, subValue, trend, color = 'emerald', onClick }) => (
    <div 
      className={`bg-slate-800 rounded-xl p-6 border border-slate-700 ${onClick ? 'cursor-pointer hover:border-slate-600' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-lg bg-${color}-500/20`}>
          <Icon className={`w-6 h-6 text-${color}-500`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-slate-400 text-sm">{label}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
        {subValue && <p className="text-slate-500 text-sm mt-1">{subValue}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400">Overview of your business metrics</p>
        </div>
        <button
          onClick={loadDashboard}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          icon={Users}
          label="TOTAL CUSTOMERS"
          value={metrics?.total_customers?.toLocaleString() || '0'}
          subValue={`↑ ${metrics?.new_this_week || 0} this week`}
          color="blue"
        />
        <MetricCard
          icon={DollarSign}
          label="MRR"
          value={`$${(metrics?.mrr || 0).toLocaleString()}`}
          subValue="Monthly Recurring Revenue"
          trend={8.3}
          color="emerald"
        />
        <MetricCard
          icon={TrendingUp}
          label="ARR"
          value={`$${(metrics?.arr || 0).toLocaleString()}`}
          subValue="Annual Recurring Revenue"
          color="purple"
        />
        <MetricCard
          icon={Activity}
          label="CHURN RATE"
          value={`${metrics?.churn_rate || 0}%`}
          subValue="Monthly churn"
          trend={-0.2}
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          icon={UserPlus}
          label="NEW SIGNUPS"
          value={metrics?.new_signups_this_month || 0}
          subValue="This month"
          trend={metrics?.signup_change_percent}
          color="cyan"
        />
        <MetricCard
          icon={CheckCircle}
          label="ACTIVE"
          value={metrics?.active_subscriptions || 0}
          subValue={`${metrics?.active_percent || 0}% of total`}
          color="emerald"
        />
        <MetricCard
          icon={AlertTriangle}
          label="PAST DUE"
          value={metrics?.past_due || 0}
          subValue="Need attention"
          color="amber"
          onClick={() => window.location.href = '/admin/subscriptions?status=past_due'}
        />
        <MetricCard
          icon={X}
          label="CANCELLED"
          value={metrics?.cancelled_this_month || 0}
          subValue="This month"
          color="red"
        />
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Monthly Recurring Revenue</h3>
          <div className="h-64 flex items-end gap-2">
            {revenueChart.map((item, i) => (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-emerald-500 rounded-t"
                  style={{ height: `${(item.mrr / 1500000) * 200}px` }}
                ></div>
                <p className="text-xs text-slate-400 mt-2">{item.month}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Plan Distribution</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">Basic ($249/mo)</span>
                <span className="text-white">{metrics?.plan_breakdown?.basic?.count || 0}</span>
              </div>
              <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500"
                  style={{ width: `${((metrics?.plan_breakdown?.basic?.count || 0) / Math.max(metrics?.total_customers || 1, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">Pro ($349/mo)</span>
                <span className="text-white">{metrics?.plan_breakdown?.pro?.count || 0}</span>
              </div>
              <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${((metrics?.plan_breakdown?.pro?.count || 0) / Math.max(metrics?.total_customers || 1, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-700">
            <h4 className="text-sm font-medium text-slate-400 mb-3">Revenue by Plan</h4>
            <div className="flex gap-4">
              <div className="flex-1 text-center">
                <p className="text-xl font-bold text-blue-500">${(metrics?.plan_breakdown?.basic?.revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-400">Basic</p>
              </div>
              <div className="flex-1 text-center">
                <p className="text-xl font-bold text-emerald-500">${(metrics?.plan_breakdown?.pro?.revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-400">Pro</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-6 bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
          <Link to="/admin/activity" className="text-emerald-500 text-sm hover:underline">View All</Link>
        </div>
        <div className="space-y-3">
          {activity.slice(0, 8).map((item, i) => (
            <div key={i} className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg">
              <span className="text-2xl">{item.icon}</span>
              <div className="flex-1">
                <p className="text-white text-sm">{item.message}</p>
                <p className="text-slate-400 text-xs">{item.details}</p>
              </div>
              <p className="text-slate-500 text-xs">
                {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CUSTOMERS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
  }, [page, search, statusFilter, planFilter]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (planFilter) params.plan = planFilter;

      const response = await AdminAPI.get('/customers', { params });
      setCustomers(response.data.customers);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/20 text-emerald-400',
      trial: 'bg-blue-500/20 text-blue-400',
      past_due: 'bg-amber-500/20 text-amber-400',
      cancelled: 'bg-red-500/20 text-red-400',
    };
    return styles[status] || 'bg-slate-500/20 text-slate-400';
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Customers</h1>
          <p className="text-slate-400">{total} total customers</p>
        </div>
        <button
          onClick={() => navigate('/admin/customers/new')}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500"
        >
          <UserPlus className="w-4 h-4" />
          Add Customer
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="past_due">Past Due</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">All Plans</option>
            <option value="basic">Basic ($249)</option>
            <option value="pro">Pro ($349)</option>
          </select>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left p-4 text-slate-400 font-medium text-sm">Company</th>
              <th className="text-left p-4 text-slate-400 font-medium text-sm">Contact</th>
              <th className="text-left p-4 text-slate-400 font-medium text-sm">Plan</th>
              <th className="text-left p-4 text-slate-400 font-medium text-sm">Status</th>
              <th className="text-left p-4 text-slate-400 font-medium text-sm">Joined</th>
              <th className="text-right p-4 text-slate-400 font-medium text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-400">No customers found</td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr
                  key={customer.user_id}
                  className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer"
                  onClick={() => navigate(`/admin/customers/${customer.user_id}`)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{customer.company_name || 'N/A'}</p>
                        <p className="text-slate-400 text-sm">{customer.company_address || ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-white">{customer.name}</p>
                    <p className="text-slate-400 text-sm">{customer.email}</p>
                  </td>
                  <td className="p-4">
                    <span className="text-white">
                      {customer.subscription?.plan === 'pro' ? 'Pro $349' : 'Basic $249'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(customer.subscription?.status)}`}>
                      {customer.subscription?.status || 'active'}
                    </span>
                  </td>
                  <td className="p-4 text-slate-400 text-sm">
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/customers/${customer.user_id}`); }}
                      className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Showing {((page - 1) * 20) + 1}-{Math.min(page * 20, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-slate-400">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// NEW CUSTOMER PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const NewCustomerPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    phone: '',
    company_address: '',
    plan: 'basic'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password || !formData.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await AdminAPI.post('/customers', formData);
      toast.success('Customer created successfully!');
      navigate(`/admin/customers/${response.data.user_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/customers')}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Add New Customer</h1>
          <p className="text-slate-400">Create a new customer account</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-4">
          <h2 className="text-lg font-semibold text-white mb-4">Account Information</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Full Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="John Smith"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="john@company.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Password <span className="text-red-400">*</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Minimum 8 characters"
              minLength={8}
              required
            />
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-4">
          <h2 className="text-lg font-semibold text-white mb-4">Company Information</h2>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Company Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="ABC Construction LLC"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Plan</label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="basic">Basic ($249/mo)</option>
                <option value="pro">Pro ($349/mo)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Company Address</label>
            <input
              type="text"
              value={formData.company_address}
              onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="123 Main St, City, State 12345"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/customers')}
            className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Create Customer
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CUSTOMER DETAIL PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const CustomerDetailPage = () => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [impersonationHistory, setImpersonationHistory] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.pathname.split('/').pop();

  useEffect(() => {
    loadCustomer();
    loadImpersonationHistory();
  }, [userId]);

  const loadCustomer = async () => {
    try {
      const response = await AdminAPI.get(`/customers/${userId}`);
      setCustomer(response.data);
    } catch (error) {
      toast.error('Failed to load customer');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
    }
  };

  const loadImpersonationHistory = async () => {
    try {
      const response = await AdminAPI.get(`/customers/${userId}/impersonation-history`);
      setImpersonationHistory(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to load impersonation history');
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateReason.trim()) {
      toast.error('Please provide a reason for accessing this account');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await AdminAPI.post('/impersonate', {
        user_id: userId,
        reason: impersonateReason,
        notify_customer: notifyCustomer
      });
      
      // Store the impersonation token
      localStorage.setItem('auth_token', response.data.impersonation_token);
      localStorage.setItem('impersonation_session_id', response.data.session_id);
      
      toast.success('Impersonation session started');
      setImpersonateModalOpen(false);
      
      // Open main app in new tab
      window.open('/dashboard', '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start impersonation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Reset this customer\'s password?')) return;
    setActionLoading(true);
    try {
      const response = await AdminAPI.post(`/customers/${userId}/reset-password`);
      toast.success(`Password reset. Temporary: ${response.data.temporary_password}`);
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePlan = async (plan) => {
    setActionLoading(true);
    try {
      await AdminAPI.post(`/subscriptions/${userId}/change-plan?plan=${plan}`);
      toast.success(`Plan changed to ${plan}`);
      loadCustomer();
    } catch (error) {
      toast.error('Failed to change plan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel this subscription?')) return;
    setActionLoading(true);
    try {
      await AdminAPI.post(`/subscriptions/${userId}/cancel`);
      toast.success('Subscription cancelled');
      loadCustomer();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    return `${mins} min${mins !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/customers')}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{customer.company_name}</h1>
          <p className="text-slate-400">{customer.email}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/admin/customers/${userId}/edit`)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            customer.subscription?.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
            customer.subscription?.status === 'past_due' ? 'bg-amber-500/20 text-amber-400' :
            customer.subscription?.status === 'trial' ? 'bg-blue-500/20 text-blue-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {customer.subscription?.status || 'Active'}
          </span>
          <span className="text-white font-medium">
            {customer.subscription?.plan === 'pro' ? 'Pro Plan' : 'Basic Plan'} - ${customer.subscription?.price || 249}/mo
          </span>
          <span className="text-slate-400">
            Since {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
          </span>
        </div>
        <p className="text-emerald-400 font-medium">LTV: ${customer.lifetime_value?.toLocaleString() || 0}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Info
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-slate-400 text-sm">Contact Name</p>
              <p className="text-white">{customer.name}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Email</p>
              <p className="text-white">{customer.email}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Phone</p>
              <p className="text-white">{customer.phone || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Address</p>
              <p className="text-white">{customer.company_address || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Billing Info */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Billing Info
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-slate-400 text-sm">Plan</p>
              <p className="text-white">{customer.subscription?.plan === 'pro' ? 'Pro' : 'Basic'} (${customer.subscription?.price || 249}/month)</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Status</p>
              <p className="text-white capitalize">{customer.subscription?.status || 'Active'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Next Bill</p>
              <p className="text-white">
                {customer.subscription?.current_period_end ? new Date(customer.subscription.current_period_end).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex gap-2 mt-6 pt-4 border-t border-slate-700">
            <button
              onClick={() => handleChangePlan(customer.subscription?.plan === 'pro' ? 'basic' : 'pro')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 disabled:opacity-50"
            >
              Change Plan
            </button>
            <button
              onClick={handleCancelSubscription}
              disabled={actionLoading || customer.subscription?.status === 'cancelled'}
              className="px-3 py-1.5 bg-red-600/20 text-red-400 text-sm rounded hover:bg-red-600/30 disabled:opacity-50"
            >
              Cancel Sub
            </button>
          </div>
        </div>

        {/* Team Members */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Members ({customer.team_members?.length || 1})
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                {customer.name?.charAt(0) || 'U'}
              </div>
              <div>
                <p className="text-white text-sm">{customer.name} (Owner)</p>
                <p className="text-slate-400 text-xs">{customer.email}</p>
              </div>
            </div>
            {customer.team_members?.map((member, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white text-sm font-medium">
                  {member.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-white text-sm">{member.name} ({member.role})</p>
                  <p className="text-slate-400 text-xs">{member.email}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Stats */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Usage Stats
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-2xl font-bold text-white">{customer.usage?.projects_created || 0}</p>
              <p className="text-slate-400 text-sm">Projects Created</p>
            </div>
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-2xl font-bold text-white">{customer.usage?.proposals_sent || 0}</p>
              <p className="text-slate-400 text-sm">Proposals Sent</p>
            </div>
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-2xl font-bold text-white">{customer.usage?.login_count || 0}</p>
              <p className="text-slate-400 text-sm">Total Logins</p>
            </div>
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-white text-sm">{customer.usage?.last_login ? new Date(customer.usage.last_login).toLocaleString() : 'Never'}</p>
              <p className="text-slate-400 text-sm">Last Login</p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Actions */}
      <div className="mt-6 bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Admin Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setImpersonateModalOpen(true)}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50"
          >
            <Key className="w-4 h-4" />
            Login as Customer
          </button>
          <button
            onClick={() => window.location.href = `mailto:${customer.email}`}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
          >
            <Mail className="w-4 h-4" />
            Send Email
          </button>
          <button
            onClick={handleResetPassword}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Password
          </button>
        </div>
      </div>

      {/* Support Access History */}
      <div className="mt-6 bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Support Access History
        </h3>
        {impersonationHistory.length === 0 ? (
          <p className="text-slate-400 text-sm">No support access history for this customer.</p>
        ) : (
          <div className="space-y-3">
            {impersonationHistory.map((session) => (
              <div key={session.session_id} className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">
                      {new Date(session.started_at).toLocaleString()}
                    </p>
                    <p className="text-slate-400 text-sm">
                      Admin: {session.super_admin_name} ({session.super_admin_email})
                    </p>
                    <p className="text-slate-400 text-sm mt-1">
                      Reason: {session.reason}
                    </p>
                    {session.resolution_notes && (
                      <p className="text-emerald-400 text-sm mt-1">
                        Resolution: {session.resolution_notes}
                      </p>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-slate-400">Duration: {formatDuration(session.duration_seconds)}</p>
                    <p className="text-slate-400">Actions: {session.total_actions || 0}</p>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      session.status === 'active' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-600 text-slate-300'
                    }`}>
                      {session.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Impersonation Confirmation Modal */}
      {impersonateModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-lg w-full border border-slate-700 shadow-2xl">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Login as Customer</h2>
              <button
                onClick={() => setImpersonateModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-slate-700/50 p-4 rounded-lg">
                <p className="text-slate-400 text-sm">You are about to log in as:</p>
                <p className="text-white font-medium mt-1">{customer.company_name}</p>
                <p className="text-slate-400 text-sm">{customer.email}</p>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
                <div className="flex gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-400 font-medium">Important</p>
                    <ul className="text-amber-200/80 mt-1 space-y-1">
                      <li>• You will see the app exactly as this customer sees it</li>
                      <li>• All actions will be logged and attributed to your admin account</li>
                      <li>• Do not make changes unless requested by the customer</li>
                      <li>• Session will expire after 4 hours</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Reason for access <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={impersonateReason}
                  onChange={(e) => setImpersonateReason(e.target.value)}
                  placeholder="E.g., Customer reported issue with Sales Pipeline not loading"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  rows={3}
                  required
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-slate-300 text-sm">Notify customer that their account is being accessed</span>
              </label>
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setImpersonateModalOpen(false)}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleImpersonate}
                disabled={actionLoading || !impersonateReason.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4" />
                    Login as Customer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUBSCRIPTIONS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const SubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({});
  const [pastDue, setPastDue] = useState([]);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subsRes, pastDueRes, trialsRes] = await Promise.all([
        AdminAPI.get('/subscriptions'),
        AdminAPI.get('/subscriptions/past-due'),
        AdminAPI.get('/subscriptions/trials')
      ]);
      setSubscriptions(subsRes.data.subscriptions);
      setStats(subsRes.data.stats);
      setPastDue(pastDueRes.data);
      setTrials(trialsRes.data);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-white mb-8">Subscriptions</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-emerald-500/20">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-white">{stats.active || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-500/20">
              <Clock className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Trial</p>
              <p className="text-2xl font-bold text-white">{stats.trial || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 cursor-pointer hover:border-amber-500/50" onClick={() => setActiveTab('past_due')}>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-amber-500/20">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Past Due</p>
              <p className="text-2xl font-bold text-white">{stats.past_due || 0}</p>
              <p className="text-amber-400 text-xs">${stats.past_due_revenue || 0} at risk</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-red-500/20">
              <X className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Cancelled</p>
              <p className="text-2xl font-bold text-white">{stats.cancelled || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        {['overview', 'past_due', 'trials'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === tab
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-700 text-slate-400 hover:text-white'
            }`}
          >
            {tab === 'past_due' ? 'Past Due' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'past_due' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Past Due Accounts ({pastDue.length})</h3>
            <button className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500">
              Send Bulk Reminder
            </button>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Company</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Amount Due</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Contact</th>
                <th className="text-right p-4 text-slate-400 font-medium text-sm">Action</th>
              </tr>
            </thead>
            <tbody>
              {pastDue.map((item) => (
                <tr key={item.user_id} className="border-b border-slate-700/50">
                  <td className="p-4">
                    <p className="text-white">{item.company_name}</p>
                    <p className="text-slate-400 text-sm">{item.email}</p>
                  </td>
                  <td className="p-4 text-amber-400 font-medium">${item.subscription?.price || 249}</td>
                  <td className="p-4 text-slate-400">{item.phone || 'N/A'}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => navigate(`/admin/customers/${item.user_id}`)}
                      className="px-3 py-1 bg-slate-700 text-white text-sm rounded hover:bg-slate-600"
                    >
                      Contact
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'trials' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Trial Users ({trials.length})</h3>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">
              Send Conversion Email
            </button>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Company</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Contact</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Trial Ends</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Usage</th>
                <th className="text-right p-4 text-slate-400 font-medium text-sm">Action</th>
              </tr>
            </thead>
            <tbody>
              {trials.map((item) => (
                <tr key={item.user_id} className="border-b border-slate-700/50">
                  <td className="p-4">
                    <p className="text-white">{item.company_name}</p>
                  </td>
                  <td className="p-4">
                    <p className="text-white">{item.name}</p>
                    <p className="text-slate-400 text-sm">{item.email}</p>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.days_left <= 3 ? 'bg-red-500/20 text-red-400' :
                      item.days_left <= 7 ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {item.days_left} days left
                    </span>
                  </td>
                  <td className="p-4 text-slate-400">
                    {item.usage?.login_count > 10 ? 'High' : item.usage?.login_count > 3 ? 'Medium' : 'Low'}
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => navigate(`/admin/customers/${item.user_id}`)}
                      className="px-3 py-1 bg-slate-700 text-white text-sm rounded hover:bg-slate-600"
                    >
                      Contact
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'overview' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">All Subscriptions</h3>
          <p className="text-slate-400">
            Total active revenue: ${(stats.active || 0) * 299} MRR (estimated average)
          </p>
          <p className="text-slate-400 mt-2">
            Trial conversion needed: {stats.trial || 0} users
          </p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// REVENUE PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const RevenuePage = () => {
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRevenue();
  }, []);

  const loadRevenue = async () => {
    try {
      const response = await AdminAPI.get('/revenue');
      setRevenue(response.data);
    } catch (error) {
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Revenue Analytics</h1>
        <button className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <p className="text-slate-400 text-sm">MRR</p>
          <p className="text-3xl font-bold text-emerald-400">${revenue?.mrr?.toLocaleString() || 0}</p>
          <p className="text-slate-500 text-sm mt-1">Monthly Recurring Revenue</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <p className="text-slate-400 text-sm">MRR Growth</p>
          <p className="text-3xl font-bold text-emerald-400">+{revenue?.mrr_growth || 0}%</p>
          <p className="text-slate-500 text-sm mt-1">vs last month</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <p className="text-slate-400 text-sm">ARPU</p>
          <p className="text-3xl font-bold text-white">${revenue?.arpu || 0}</p>
          <p className="text-slate-500 text-sm mt-1">Avg Revenue Per User</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <p className="text-slate-400 text-sm">Net Revenue Retention</p>
          <p className="text-3xl font-bold text-emerald-400">{revenue?.net_revenue_retention || 0}%</p>
          <p className="text-slate-500 text-sm mt-1">Excellent</p>
        </div>
      </div>

      {/* MRR Breakdown */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
        <h3 className="text-lg font-semibold text-white mb-4">MRR Breakdown</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-slate-700">
            <span className="text-slate-400">New MRR (new customers)</span>
            <span className="text-emerald-400 font-medium">+ ${revenue?.breakdown?.new_mrr?.toLocaleString() || 0}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-700">
            <span className="text-slate-400">Expansion MRR (upgrades)</span>
            <span className="text-emerald-400 font-medium">+ ${revenue?.breakdown?.expansion_mrr?.toLocaleString() || 0}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-700">
            <span className="text-slate-400">Contraction MRR (downgrades)</span>
            <span className="text-red-400 font-medium">- ${revenue?.breakdown?.contraction_mrr?.toLocaleString() || 0}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-700">
            <span className="text-slate-400">Churned MRR (cancellations)</span>
            <span className="text-red-400 font-medium">- ${revenue?.breakdown?.churned_mrr?.toLocaleString() || 0}</span>
          </div>
          <div className="flex justify-between items-center py-2 font-semibold">
            <span className="text-white">Net New MRR</span>
            <span className="text-emerald-400">+ ${(
              (revenue?.breakdown?.new_mrr || 0) +
              (revenue?.breakdown?.expansion_mrr || 0) -
              (revenue?.breakdown?.contraction_mrr || 0) -
              (revenue?.breakdown?.churned_mrr || 0)
            ).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Revenue by Plan */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Revenue by Plan</h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Basic Plan ($249/mo)</span>
              <span className="text-white">{revenue?.by_plan?.basic?.users || 0} users - ${revenue?.by_plan?.basic?.revenue?.toLocaleString() || 0} ({revenue?.by_plan?.basic?.percent || 0}%)</span>
            </div>
            <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500"
                style={{ width: `${revenue?.by_plan?.basic?.percent || 0}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Pro Plan ($349/mo)</span>
              <span className="text-white">{revenue?.by_plan?.pro?.users || 0} users - ${revenue?.by_plan?.pro?.revenue?.toLocaleString() || 0} ({revenue?.by_plan?.pro?.percent || 0}%)</span>
            </div>
            <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${revenue?.by_plan?.pro?.percent || 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const ActivityPage = () => {
  const [activity, setActivity] = useState([]);
  const [adminLog, setAdminLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('system');

  useEffect(() => {
    loadActivity();
  }, []);

  const loadActivity = async () => {
    try {
      const [activityRes, logRes] = await Promise.all([
        AdminAPI.get('/dashboard/activity?limit=50'),
        AdminAPI.get('/activity-log?limit=50')
      ]);
      setActivity(activityRes.data);
      setAdminLog(logRes.data);
    } catch (error) {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Activity Log</h1>
        <button
          onClick={loadActivity}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('system')}
          className={`px-4 py-2 rounded-lg font-medium ${
            activeTab === 'system' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
          }`}
        >
          System Activity
        </button>
        <button
          onClick={() => setActiveTab('admin')}
          className={`px-4 py-2 rounded-lg font-medium ${
            activeTab === 'admin' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
          }`}
        >
          Admin Actions
        </button>
      </div>

      {activeTab === 'system' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              <span className="text-slate-400 text-sm">Live Activity Feed</span>
            </div>
          </div>
          <div className="divide-y divide-slate-700">
            {activity.map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-4 hover:bg-slate-700/30">
                <span className="text-2xl">{item.icon}</span>
                <div className="flex-1">
                  <p className="text-white">{item.message}</p>
                  <p className="text-slate-400 text-sm">{item.details}</p>
                </div>
                <p className="text-slate-500 text-sm">
                  {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'admin' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Time</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Admin</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Action</th>
                <th className="text-left p-4 text-slate-400 font-medium text-sm">Details</th>
              </tr>
            </thead>
            <tbody>
              {adminLog.map((item, i) => (
                <tr key={i} className="border-b border-slate-700/50">
                  <td className="p-4 text-slate-400 text-sm">
                    {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                  </td>
                  <td className="p-4 text-white">{item.admin_id}</td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                      {item.action}
                    </span>
                  </td>
                  <td className="p-4 text-slate-400 text-sm">
                    {JSON.stringify(item.details)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// IMPERSONATION LOGS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const ImpersonationLogsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, [page]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await AdminAPI.get('/impersonation-logs', { params: { page, limit: 20 } });
      setSessions(response.data.sessions);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Failed to load impersonation logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    return `${mins} min${mins !== 1 ? 's' : ''}`;
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Impersonation Audit Log</h1>
        <p className="text-slate-400">Track all super admin access to customer accounts</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-slate-800 rounded-xl p-8 border border-slate-700 text-center">
          <Eye className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No impersonation sessions recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map((session) => (
            <div
              key={session.session_id}
              className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
              onClick={() => setSelectedSession(session)}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      session.status === 'active' 
                        ? 'bg-amber-500/20 text-amber-400' 
                        : 'bg-slate-600 text-slate-300'
                    }`}>
                      {session.status}
                    </span>
                    <span className="text-white font-medium">
                      {new Date(session.started_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-slate-400">
                    <span className="text-white">{session.super_admin_name}</span>
                    {' '}({session.super_admin_email})
                  </p>
                  <p className="text-slate-400">
                    Customer: <span className="text-white">{session.target_company_name}</span>
                    {' '}({session.target_user_email})
                  </p>
                  <p className="text-slate-400 text-sm">
                    Reason: {session.reason}
                  </p>
                  {session.resolution_notes && (
                    <p className="text-emerald-400 text-sm">
                      Resolution: {session.resolution_notes}
                    </p>
                  )}
                </div>
                <div className="text-right text-sm space-y-1">
                  <p className="text-slate-400">Duration: {formatDuration(session.duration_seconds)}</p>
                  <p className="text-slate-400">Actions: {session.total_actions || 0}</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/customers/${session.target_user_id}`);
                    }}
                    className="text-emerald-400 hover:text-emerald-300 text-xs"
                  >
                    View Customer →
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-slate-400 px-4">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full border border-slate-700 shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Session Details</h2>
              <button
                onClick={() => setSelectedSession(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Session ID</p>
                  <p className="text-white font-mono text-sm">{selectedSession.session_id}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Status</p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    selectedSession.status === 'active' 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {selectedSession.status}
                  </span>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Admin</p>
                  <p className="text-white">{selectedSession.super_admin_name}</p>
                  <p className="text-slate-400 text-xs">{selectedSession.super_admin_email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Customer</p>
                  <p className="text-white">{selectedSession.target_company_name}</p>
                  <p className="text-slate-400 text-xs">{selectedSession.target_user_email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Started</p>
                  <p className="text-white">{new Date(selectedSession.started_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Duration</p>
                  <p className="text-white">{formatDuration(selectedSession.duration_seconds)}</p>
                </div>
              </div>

              <div>
                <p className="text-slate-400 text-sm">Reason</p>
                <p className="text-white">{selectedSession.reason}</p>
              </div>

              {selectedSession.resolution_notes && (
                <div>
                  <p className="text-slate-400 text-sm">Resolution Notes</p>
                  <p className="text-emerald-400">{selectedSession.resolution_notes}</p>
                </div>
              )}

              {selectedSession.actions_logged && selectedSession.actions_logged.length > 0 && (
                <div>
                  <p className="text-slate-400 text-sm mb-2">Actions ({selectedSession.total_actions})</p>
                  <div className="bg-slate-700/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {selectedSession.actions_logged.map((action, i) => (
                      <div key={i} className="text-sm py-1 border-b border-slate-600 last:border-0">
                        <span className="text-slate-400">{new Date(action.timestamp).toLocaleTimeString()}</span>
                        <span className="text-white ml-2">{action.action}</span>
                        <span className="text-slate-400 ml-2">{action.path}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-end">
              <button
                onClick={() => setSelectedSession(null)}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SETTINGS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await AdminAPI.get('/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const IntegrationCard = ({ name, icon, connected, details }) => (
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-slate-700">
            {icon}
          </div>
          <div>
            <h4 className="text-white font-medium">{name}</h4>
            <p className={`text-sm ${connected ? 'text-emerald-400' : 'text-red-400'}`}>
              {connected ? '✓ Connected' : '✗ Not Connected'}
            </p>
          </div>
        </div>
      </div>
      {details && (
        <div className="text-slate-400 text-sm space-y-1">
          {Object.entries(details).map(([key, value]) => (
            <p key={key}>{key}: {value}</p>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-white mb-8">Settings</h1>

      {/* Integrations */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Integrations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <IntegrationCard
            name="Stripe Payments"
            icon={<CreditCard className="w-6 h-6 text-slate-400" />}
            connected={settings?.integrations?.stripe?.connected}
            details={{ Mode: settings?.integrations?.stripe?.mode || 'N/A' }}
          />
          <IntegrationCard
            name="Resend Email"
            icon={<Mail className="w-6 h-6 text-slate-400" />}
            connected={settings?.integrations?.resend?.connected}
            details={{ From: settings?.integrations?.resend?.from_email || 'N/A' }}
          />
          <IntegrationCard
            name="Twilio SMS"
            icon={<Phone className="w-6 h-6 text-slate-400" />}
            connected={settings?.integrations?.twilio?.connected}
            details={{ Phone: settings?.integrations?.twilio?.phone || 'N/A' }}
          />
          <IntegrationCard
            name="Plaid Banking"
            icon={<Building2 className="w-6 h-6 text-slate-400" />}
            connected={settings?.integrations?.plaid?.connected}
            details={{ Environment: settings?.integrations?.plaid?.env || 'N/A' }}
          />
        </div>
      </div>

      {/* Admin Users */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Admin Users</h2>
          <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500">
            <UserPlus className="w-4 h-4" />
            Add Admin
          </button>
        </div>
        <div className="space-y-3">
          {settings?.admin_users?.map((admin) => (
            <div key={admin.admin_id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-semibold">
                  {admin.name?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="text-white font-medium">{admin.name}</p>
                  <p className="text-slate-400 text-sm">{admin.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-slate-600 text-slate-300 text-xs rounded capitalize">
                  {admin.role}
                </span>
                <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-600 rounded">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ADMIN ROUTER
// ═══════════════════════════════════════════════════════════════════════════════

const AdminRouter = () => {
  return (
    <Routes>
      <Route path="login" element={<AdminLoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedAdminRoute>
            <AdminLayout>
              <Routes>
                <Route index element={<DashboardPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="customers/new" element={<NewCustomerPage />} />
                <Route path="customers/:userId" element={<CustomerDetailPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="revenue" element={<RevenuePage />} />
                <Route path="impersonation-logs" element={<ImpersonationLogsPage />} />
                <Route path="activity" element={<ActivityPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Routes>
            </AdminLayout>
          </ProtectedAdminRoute>
        }
      />
    </Routes>
  );
};

// Main Export
const AdminDashboard = () => {
  return (
    <AdminAuthProvider>
      <AdminRouter />
    </AdminAuthProvider>
  );
};

export default AdminDashboard;
